import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useFormik } from 'formik';
import { Alert, Box, CircularProgress, Grid, Snackbar, Stack, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Save } from '@mui/icons-material';

import { getFormularioProdutor, salvarCadastroProdutor, verificarDocumentoProdutor } from '../../../api/produtores';
import useTenant from '../../../hooks/useTenant';
import { extrairMensagemErro } from '../../../utils/apiError';
import { CnpjValido, CpfValido } from '../../../utils/validaDocumento';
import AutorizacaoUsoImagemDialog from '../../dialogs/confirmacao-cadastro/AutorizacaoUsoImagemDialog';
import ConfirmacaoCadastroProdutorDialog from '../../dialogs/confirmacao-cadastro/ConfirmacaoCadastroProdutorDialog';
import CampoCadastro from './CampoCadastro';
import useAutoSave from './useAutoSave';
import useCamposFaltando from './useCamposFaltando';
import {
  faltaAceiteObrigatorio,
  ocupaLinhaInteira,
  prepararCampos,
  serializarValores,
  valorForcado,
  valoresIniciais,
} from './regrasCadastro';

const somenteDigitos = (valor) => String(valor ?? '').replace(/\D/g, '');

/**
 * Erro do CPF/CNPJ digitado: dígito inválido na hora; duplicidade (outro produtor
 * já usa o documento) consultada no backend 1s depois que o usuário para de digitar.
 * `idCadastro` exclui o próprio produtor da checagem. Devolve a mensagem ou null.
 */
function useErroDocumento(valor, { tamanho, valido, idCadastro, mensagens }) {
  const digitos = somenteDigitos(valor);
  const completo = digitos.length === tamanho;
  const invalido = completo && !valido(digitos);
  const [consulta, setConsulta] = useState({ digitos: null, erro: null });

  useEffect(() => {
    if (!completo || invalido) return undefined;
    let ativo = true;
    const espera = setTimeout(() => {
      verificarDocumentoProdutor(digitos, idCadastro)
        // data = true: o documento já existe em outro cadastro
        .then((res) => ativo && setConsulta({ digitos, erro: res.data.data ? 'duplicado' : null }))
        .catch(() => ativo && setConsulta({ digitos, erro: 'falha' }));
    }, 1000);
    return () => {
      ativo = false;
      clearTimeout(espera);
    };
  }, [digitos, completo, invalido, idCadastro]);

  if (!completo) return null;
  if (invalido) return mensagens.invalido;
  if (consulta.digitos !== digitos || !consulta.erro) return null;
  return mensagens[consulta.erro];
}

/**
 * Cadastro do produtor no formulário configurado pelo cliente. Salva rascunho
 * ("Pendente") a cada mudança e envia "Cadastrado" no botão final do wizard.
 * Os aceites de termos e de uso de imagem passam pelos diálogos com o texto legal.
 */
export default function FormularioProdutor({ onSuccess, wizardMode = false, initialEmail = '' }) {
  const { t, i18n } = useTranslation();
  const { slug: slugDaUrl } = useParams();
  const { selectedSlug, bootstrapLoaded } = useTenant();
  const slug = slugDaUrl || selectedSlug || import.meta.env.VITE_DEV_TENANT_SLUG || null;

  // O backend devolve labels/opções no idioma pedido: troca de idioma recarrega o formulário.
  const chaveCarga = slug && bootstrapLoaded ? `${slug}|${i18n.language}` : null;
  const [carga, setCarga] = useState(null); // { chave, formulario } | { chave, falhou: true }
  const [idCadastro, setIdCadastro] = useState(null); // criado pelo 1º rascunho
  const [aviso, setAviso] = useState(null); // { severity, message } do Snackbar
  const [termoPendente, setTermoPendente] = useState(null); // código do aceite à espera do diálogo
  const [concluido, setConcluido] = useState(false);

  useEffect(() => {
    if (!chaveCarga) return undefined;
    let ativo = true;
    getFormularioProdutor(slug, i18n.language)
      .then((res) => {
        const dados = res.data.data;
        if (ativo) setCarga({ chave: chaveCarga, formulario: { ...dados, campos: prepararCampos(dados.campos) } });
      })
      .catch(() => ativo && setCarga({ chave: chaveCarga, falhou: true }));
    return () => {
      ativo = false;
    };
  }, [chaveCarga]); // eslint-disable-line react-hooks/exhaustive-deps -- slug e idioma já estão na chave

  const atual = carga?.chave === chaveCarga ? carga : null;
  const formulario = atual?.formulario ?? null;
  const campos = useMemo(() => formulario?.campos ?? [], [formulario]);
  const cadastroId = idCadastro ?? formulario?.produtorCadastroId;
  const faltantes = useCamposFaltando(campos);

  const enviar = (valores, status) =>
    salvarCadastroProdutor({
      versaoFormulario: formulario.versao,
      status,
      valores: serializarValores(campos, valores),
    });

  const rascunho = useAutoSave(async (valores) => {
    const res = await enviar(valores, 'Pendente');
    // O 1º rascunho cria o cadastro: o id passa a excluir o próprio produtor na checagem de CPF/CNPJ
    const novoId = res?.data?.data?.produtorCadastroId;
    if (novoId && !cadastroId) setIdCadastro(novoId);
  });

  const iniciais = useMemo(
    () => (formulario ? valoresIniciais(campos, formulario.valores, initialEmail) : {}),
    [formulario, campos, initialEmail],
  );

  const formik = useFormik({
    initialValues: iniciais,
    enableReinitialize: true,
    onSubmit: async (valores, { setSubmitting, setStatus }) => {
      rascunho.cancelar(); // um rascunho atrasado sobrescreveria o "Cadastrado"
      try {
        await enviar(valores, 'Cadastrado');
        setStatus(null);
        setAviso({ severity: 'success', message: t('validacao.cadastroSalvo') });
        if (onSuccess) {
          // Segura o spinner até o wizard trocar de etapa
          setConcluido(true);
          onSuccess();
        }
      } catch (erro) {
        const mensagem = faltantes.registrar(erro) ?? extrairMensagemErro(erro, t('validacao.erroSalvarCadastro'));
        setStatus({ erro: mensagem });
        setAviso({ severity: 'error', message: mensagem });
      } finally {
        setSubmitting(false);
      }
    },
  });
  const { values, setFieldValue } = formik;

  // Regra forcarValorSe: mantém no Formik o valor travado (ex.: industrial → possui_selo).
  useEffect(() => {
    for (const campo of campos) {
      const forcado = valorForcado(campo, values);
      if (forcado !== null && values[campo.codigo] !== forcado) setFieldValue(campo.codigo, forcado);
    }
  }, [campos, values, setFieldValue]);

  const erroCpf = useErroDocumento(values.cpf, {
    tamanho: 11,
    valido: CpfValido,
    idCadastro: cadastroId,
    mensagens: {
      invalido: t('validacao.cpfInvalido'),
      duplicado: t('validacao.cpfJaCadastradoProdutor'),
      falha: t('validacao.cpfErroVerificar'),
    },
  });
  const erroCnpj = useErroDocumento(values.cnpj, {
    tamanho: 14,
    valido: CnpjValido,
    idCadastro: cadastroId,
    mensagens: {
      invalido: t('validacao.cnpjInvalido'),
      duplicado: t('validacao.cnpjJaCadastradoProdutor'),
      falha: t('validacao.cnpjErroVerificar'),
    },
  });

  const alterar = (mudancas) => {
    faltantes.limpar(Object.keys(mudancas));
    Object.entries(mudancas).forEach(([codigo, valor]) => setFieldValue(codigo, valor));
  };
  const alterarESalvar = (mudancas) => {
    alterar(mudancas);
    rascunho.agendar({ ...values, ...mudancas });
  };

  // Termos e uso de imagem só são aceitos depois de ler o texto no diálogo.
  const alternarAceite = (codigo, marcado) => {
    const c = codigo.toLowerCase();
    if (marcado && (c.includes('termos') || c.includes('aceite_imagem'))) setTermoPendente(codigo);
    else alterarESalvar({ [codigo]: marcado });
  };
  const responderTermo = (aceito) => {
    alterarESalvar({ [termoPendente]: aceito });
    setTermoPendente(null);
  };
  const termoAberto = termoPendente?.toLowerCase() ?? '';

  if (!slug) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px" p={4}>
        <Alert severity="warning" sx={{ maxWidth: 480 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={1}>
            {t('validacao.linkInvalido.titulo')}
          </Typography>
          <Typography variant="body2">{t('validacao.linkInvalido.texto')}</Typography>
        </Alert>
      </Box>
    );
  }
  if (atual?.falhou) return <Alert severity="error">{t('cadastro.erroCarregarFormulario')}</Alert>;
  if (!formulario || formik.isSubmitting || concluido) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Helmet title={t('Producer Registration')} />

      <ConfirmacaoCadastroProdutorDialog
        open={termoAberto.includes('termos')}
        onClose={() => responderTermo(false)}
        setChecked={() => responderTermo(true)}
      />
      <AutorizacaoUsoImagemDialog
        open={termoAberto.includes('aceite_imagem')}
        onClose={() => responderTermo(false)}
        setChecked={() => responderTermo(true)}
        nomeRepresentante={values.representante_legal}
        rg={values.rg}
        cpf={values.cpf}
      />
      <Snackbar
        open={Boolean(aviso)}
        autoHideDuration={5000}
        onClose={() => setAviso(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setAviso(null)} severity={aviso?.severity} variant="filled" sx={{ width: '100%' }}>
          {aviso?.message}
        </Alert>
      </Snackbar>

      <Box component="form" onSubmit={formik.handleSubmit} noValidate>
        <Stack spacing={2.5}>
          {formik.status?.erro && <Alert severity="error">{formik.status.erro}</Alert>}

          <Box>
            {/* Box: o Stack zera a margem dos filhos e anularia a margem negativa do Grid */}
            <Grid container spacing={2.5}>
              {campos.map((campo) => (
                <Grid
                  item
                  xs={12}
                  md={ocupaLinhaInteira(campo) ? 12 : 6}
                  key={campo.codigo}
                  sx={{ '&:empty': { display: 'none' } }}
                >
                  <CampoCadastro
                    campo={campo}
                    valores={values}
                    valoresSalvos={formulario.valores}
                    alterar={alterar}
                    alterarESalvar={alterarESalvar}
                    aoSair={() => rascunho.agendar(values)}
                    aoAlternar={alternarAceite}
                    erro={{ cpf: erroCpf, cnpj: erroCnpj }[campo.codigo] ?? faltantes.erroDe(campo.codigo)}
                    emailFixo={Boolean(initialEmail)}
                    logradouroSemNome="Área rural"
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          {rascunho.falhou && (
            <Typography variant="caption" color="error.main" textAlign="right">
              {t('Error saving changes.')}
            </Typography>
          )}

          {wizardMode && (
            <LoadingButton
              type="submit"
              variant="contained"
              fullWidth
              loading={formik.isSubmitting}
              startIcon={<Save />}
              disabled={faltaAceiteObrigatorio(campos, values)}
              sx={{ height: 48 }}
            >
              {t('Register')}
            </LoadingButton>
          )}
        </Stack>
      </Box>
    </>
  );
}
